<template>
  <div>
    <div class="d-flex align-items-center justify-content-between mb-3">
      <h6 class="my-0 font-weight-bold srs-heading">Saved Reports &amp; Scheduling</h6>

      <div class="d-flex align-items-center">
        <base-button type="primary" class="mr-2" @click="onFullExport"
          >Full Data Export</base-button
        >
        <base-button @click="() => selectItem(null)" type="primary"
          >New Report</base-button
        >
      </div>
    </div>

    <b-table
      v-if="filteredRows.length"
      :items="filteredRows"
      :fields="tableFields"
      primary-key="id"
      class="mb-0 srs-table"
      responsive
      hover
      @row-clicked="(row) => selectItem(row.id)"
    >
      <!-- Column header: label + (for searchable columns) a search icon that
           opens a per-column search popover. Mirrors the Export Log page. -->
      <template #head()="{ label, field }">
        <span class="d-inline-flex align-items-center">
          {{ label }}
          <template v-if="field.searchable">
            <b-button
              :id="`srs-hsearch-${field.key}`"
              variant="link"
              size="sm"
              class="p-0 ml-1 header-search-btn"
              :class="{ 'is-active': hasColumnSearch(field.key) }"
              @click.stop
            >
              <b-icon icon="search" font-scale="0.8"></b-icon>
            </b-button>
            <b-popover
              :target="`srs-hsearch-${field.key}`"
              triggers="click blur"
              placement="bottom"
              custom-class="header-search-popover"
            >
              <b-form @submit.prevent="applyColumnSearch(field.key)">
                <!-- Dropdown for status / export type -->
                <b-form-select
                  v-if="field.searchType === 'select'"
                  v-model="columnSearchDraft[field.key]"
                  :options="selectOptionsFor(field.key)"
                  size="sm"
                  class="mb-2"
                ></b-form-select>

                <!-- Date range for next run -->
                <template v-else-if="field.searchType === 'daterange'">
                  <label class="small mb-1 text-muted">From</label>
                  <b-form-input
                    v-model="columnSearchDraft[field.key].from"
                    type="date"
                    size="sm"
                    class="mb-2"
                  ></b-form-input>
                  <label class="small mb-1 text-muted">To</label>
                  <b-form-input
                    v-model="columnSearchDraft[field.key].to"
                    type="date"
                    size="sm"
                    class="mb-2"
                  ></b-form-input>
                </template>

                <!-- Free text otherwise -->
                <b-form-input
                  v-else
                  v-model="columnSearchDraft[field.key]"
                  :placeholder="`Search by ${label}`"
                  size="sm"
                  class="mb-2"
                  autofocus
                ></b-form-input>

                <div class="d-flex">
                  <b-button type="submit" size="sm" class="limeiq-apply-btn">
                    Apply
                  </b-button>
                  <b-button
                    v-if="hasColumnSearch(field.key)"
                    size="sm"
                    variant="link"
                    class="ml-2 text-muted"
                    @click="clearColumnSearch(field.key)"
                  >
                    Clear
                  </b-button>
                </div>
              </b-form>
            </b-popover>
          </template>
        </span>
      </template>

      <template #cell(name)="{ item }">
        <span class="font-weight-bold">{{ item.name }}</span>
      </template>

      <template #cell(exportType)="{ item }">
        <span v-if="item.exportType">{{ item.exportType }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(frequency)="{ item }">
        <span v-if="item.frequency">{{ item.frequency }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(nextRun)="{ item }">
        <span v-if="item.nextRun">{{ formatDay(item.nextRun) }}</span>
        <span v-else class="text-muted">—</span>
      </template>

      <template #cell(status)="{ item }">
        <b-badge
          :variant="statusVariant(item.status)"
          pill
          class="px-3 py-1"
          :class="`status-badge status-${(item.status || '').toLowerCase()}`"
        >
          {{ item.status }}
        </b-badge>
      </template>

      <!--
        One table for saved reports AND schedules (feedback #6).
        Actions adapt to the row's status:
          Saved     → Amend · Delete (no live schedule — either never scheduled,
                      or the schedule finished its window; Amend re-opens it to
                      re-run or re-schedule)
          Cancelled → Reactivate · Delete
          Active    → Amend · Pause · Cancel · Delete
          Paused    → Amend · Resume · Cancel · Delete
      -->
      <template #cell(actions)="{ item }">
        <div class="d-flex align-items-center action-links" @click.stop>
          <!-- Text actions live in a fixed-width block so the trash icon that
               follows always lands in the same column, whether the row has a
               full Amend·Pause·Cancel set or nothing at all. -->
          <div class="action-text">
            <!-- Saved report with no schedule, or a schedule whose window has
                 ended: no live schedule to pause/cancel, but Amend re-opens the
                 report to re-run it or attach a new schedule. -->
            <template v-if="!item.scheduled || item.status === 'Saved'">
              <a href @click.prevent="amendSchedule(item)" class="action-brand"
                >Amend</a
              >
            </template>
            <template v-else-if="item.status === 'Cancelled'">
              <a href @click.prevent="activateSchedule(item)" class="action-brand"
                >Reactivate</a
              >
            </template>
            <template v-else>
              <a href @click.prevent="amendSchedule(item)" class="action-brand"
                >Amend</a
              >
              <span class="action-sep">|</span>
              <a
                v-if="item.status === 'Paused'"
                href
                @click.prevent="resumeSchedule(item)"
                class="action-brand"
                >Resume</a
              >
              <a v-else href @click.prevent="pauseSchedule(item)" class="action-brand"
                >Pause</a
              >
              <span class="action-sep">|</span>
              <a href @click.prevent="cancelSchedule(item)" class="action-brand"
                >Cancel</a
              >
            </template>
          </div>
          <a
            href
            @click.prevent="deleteItem(item.id)"
            class="text-danger action-delete"
            title="Delete"
          >
            <b-icon icon="trash"></b-icon>
          </a>
        </div>
      </template>
    </b-table>
    <p v-else class="mb-0">No saved reports found.</p>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted
} from '@vue/composition-api'
import { createVuexHelpers } from 'vue2-helpers'
import { useRouter } from 'vue2-helpers/vue-router'
import moment from 'moment'

import store from '@store'
import { ExportsService } from '@services'
import ExportLogService from '@services/exportLogService'

const STATUS_VARIANTS = {
  Active: 'success',
  Paused: 'secondary',
  Cancelled: 'danger',
  Saved: 'info'
}
const EXPORT_TYPE_LABELS = {
  market_export: 'Market Split',
  custom_export: 'Custom Split'
}

// Saved reports come from /saved_exports/ (SavedReportTemplate rows), whose
// `payload` is a JSON string holding export_type / is_full_data_export.
const parsePayload = (payload) => {
  if (!payload) return {}
  if (typeof payload === 'object') return payload
  try {
    return JSON.parse(payload)
  } catch (e) {
    return {}
  }
}

const exportTypeLabel = (params) => {
  if (params && params.is_full_data_export) return 'Full Data Export'
  const code = params && params.export_type
  if (!code) return null
  return EXPORT_TYPE_LABELS[code] || code
}

// A SavedReportTemplate row carries both a saved report (name + payload) and,
// optionally, a schedule (occurrence + cadence fields). A row with no
// occurrence is a saved report you re-run on demand — it has no live schedule,
// so it reads "Saved" and offers no pause/cancel.
const isScheduled = (item) => !!item.occurrence

// A schedule that is enabled and not cancelled but has NO next run is finished
// — the export has run and nothing more will fire, so it reads "Saved" (not
// "Active"). The backend is authoritative: it returns next_run=null once no
// further run will happen. (Checked AFTER the paused case, since a paused
// schedule still reports a computed next_run.)
const isExpired = (item) => {
  if (!isScheduled(item) || item.cancelled || !item.status) return false
  return !item.next_run
}

const scheduleStatus = (item) => {
  // A row with no schedule, or a schedule that has run out its window
  // (next_run null → nothing more will fire), both read "Saved": the report
  // template is still saved and re-runnable, there is just no live schedule.
  if (!isScheduled(item)) return 'Saved'
  if (item.cancelled) return 'Cancelled'
  if (!item.status) return 'Paused'
  if (isExpired(item)) return 'Saved'
  return 'Active'
}

const STATUS_OPTIONS = ['Active', 'Paused', 'Cancelled', 'Saved']
const EXPORT_TYPE_OPTIONS = ['Market Split', 'Custom Split', 'Full Data Export']

export default defineComponent({
  name: 'SaveReportWizardReports',
  setup(_, { emit }) {
    const { useGetters } = createVuexHelpers()
    const { exportsList } = useGetters(['exportsList'])
    const router = useRouter()

    const isProcessing = ref(false)
    const selectedItem = ref()

    // Trimmed, Saved-Reports-focused columns (feedback #6): Name, Export Type,
    // Frequency, Next Run, Status, Actions. Modelled on the Export Log table,
    // with the same per-column header search affordances.
    const tableFields = [
      { key: 'name', label: 'Name', searchable: true, searchType: 'text', thStyle: { width: '240px' } },
      { key: 'exportType', label: 'Export Type', searchable: true, searchType: 'select', thStyle: { width: '150px' } },
      { key: 'frequency', label: 'Frequency', searchable: true, searchType: 'select', thStyle: { width: '110px' } },
      { key: 'nextRun', label: 'Next Run', searchable: true, searchType: 'daterange', thStyle: { width: '150px' } },
      { key: 'status', label: 'Status', searchable: true, searchType: 'select', thStyle: { width: '100px' } },
      { key: 'actions', label: 'Actions', thStyle: { width: '220px' } }
    ]

    const rows = computed(() =>
      exportsList.value.map((item) => {
        const params = parsePayload(item.payload)
        return {
          id: item.id,
          name: item.name,
          exportType: exportTypeLabel(params),
          scheduled: isScheduled(item),
          frequency: item.occurrence || null,
          nextRun: item.next_run || null,
          status: scheduleStatus(item)
        }
      })
    )

    // ---- Per-column search (mirrors the Export Log page) -----------------
    const columnSearch = reactive({
      name: '',
      exportType: '',
      frequency: '',
      status: '',
      nextRun: { from: '', to: '' }
    })
    const columnSearchDraft = reactive({
      name: '',
      exportType: '',
      frequency: '',
      status: '',
      nextRun: { from: '', to: '' }
    })
    const DATE_COLUMNS = ['nextRun']

    const selectOptionsFor = (key) => {
      const base = [{ value: '', text: 'All' }]
      if (key === 'status')
        return base.concat(STATUS_OPTIONS.map((s) => ({ value: s, text: s })))
      if (key === 'exportType')
        return base.concat(EXPORT_TYPE_OPTIONS.map((s) => ({ value: s, text: s })))
      if (key === 'frequency')
        return base.concat(
          ['Daily', 'Weekly', 'Monthly'].map((s) => ({ value: s, text: s }))
        )
      return base
    }

    const applyColumnSearch = (key) => {
      if (DATE_COLUMNS.includes(key)) {
        columnSearch[key] = {
          from: columnSearchDraft[key].from || '',
          to: columnSearchDraft[key].to || ''
        }
      } else {
        columnSearch[key] = (columnSearchDraft[key] || '').trim()
      }
    }

    const clearColumnSearch = (key) => {
      if (DATE_COLUMNS.includes(key)) {
        columnSearch[key] = { from: '', to: '' }
        columnSearchDraft[key] = { from: '', to: '' }
      } else {
        columnSearch[key] = ''
        columnSearchDraft[key] = ''
      }
    }

    const hasColumnSearch = (key) => {
      const v = columnSearch[key]
      if (DATE_COLUMNS.includes(key)) return !!(v && (v.from || v.to))
      return !!v
    }

    const columnValue = (row, key) => {
      switch (key) {
        case 'name':
          return row.name || ''
        case 'exportType':
          return row.exportType || ''
        case 'frequency':
          return row.frequency || ''
        case 'status':
          return row.status || ''
        default:
          return ''
      }
    }

    const dateInRange = (row, key) => {
      const range = columnSearch[key]
      const value = row[key]
      if (!value) return false
      const d = moment(value)
      if (range.from && d.isBefore(moment(range.from), 'day')) return false
      if (range.to && d.isAfter(moment(range.to), 'day')) return false
      return true
    }

    const filteredRows = computed(() => {
      const activeKeys = Object.keys(columnSearch).filter(hasColumnSearch)
      if (!activeKeys.length) return rows.value
      return rows.value.filter((row) => {
        for (const key of activeKeys) {
          if (DATE_COLUMNS.includes(key)) {
            if (!dateInRange(row, key)) return false
          } else {
            const needle = String(columnSearch[key]).trim().toLowerCase()
            // select columns match exactly; text columns match by "contains".
            if (['status', 'exportType', 'frequency'].includes(key)) {
              if (columnValue(row, key).toLowerCase() !== needle) return false
            } else if (!columnValue(row, key).toLowerCase().includes(needle)) {
              return false
            }
          }
        }
        return true
      })
    })

    const formatDay = (date) =>
      date ? moment(date).format('MMM D, YYYY') : '—'

    const statusVariant = (status) => STATUS_VARIANTS[status] || 'light'

    const selectItem = (id) => {
      if (!id) selectedItem.value = null
      emit('select', id)
    }

    const onFullExport = () => emit('full-export')

    const deleteItem = async (id) => {
      await ExportsService.deleteExport(id)
      Vue.prototype.$toasted.show('Saved export deleted.')
    }

    // --- Scheduled export actions (modifiable from the Export page) --------
    const amendSchedule = (item) => {
      // Update the ?amend query; the wizard watches it and loads the template.
      // We may already be on the Export page (same-route push), so ignore the
      // expected NavigationDuplicated rejection. If the query is already this
      // id, push won't fire the watcher — emit('select') directly instead.
      const current = router.currentRoute
      if (String(current.query.amend) === String(item.id)) {
        emit('select', item.id)
        return
      }
      router
        .push({ name: 'Export', query: { amend: item.id } })
        .catch((err) => {
          if (err && err.name !== 'NavigationDuplicated') throw err
        })
    }

    const runScheduleAction = async (item, action, fn, successMsg, patch) => {
      try {
        await fn(item.id)
        Vue.prototype.$toasted.show(successMsg, { type: 'success' })
        // Update ONLY the affected row in place (matches what the backend just
        // did) instead of refetching+replacing the whole list. A full refetch
        // rebuilt every row and made b-table flash the wrong status on the
        // neighbouring row; patching one row re-renders just that row.
        if (patch) store.commit('PATCH_EXPORT', { id: item.id, fields: patch })
      } catch (err) {
        Vue.prototype.$toasted.show(
          `Could not ${action} the schedule. Please try again.`,
          { type: 'error' }
        )
      }
    }

    const pauseSchedule = (item) =>
      runScheduleAction(
        item,
        'pause',
        (id) => ExportLogService.pause(id),
        'Schedule paused.',
        // Backend: status=False (schedule.status = False)
        { status: false }
      )

    const resumeSchedule = (item) =>
      runScheduleAction(
        item,
        'resume',
        (id) => ExportLogService.resume(id),
        'Schedule resumed.',
        // Backend: status=True, cancelled=False
        { status: true, cancelled: false }
      )

    const activateSchedule = (item) =>
      runScheduleAction(
        item,
        'activate',
        (id) => ExportLogService.activate(id),
        'Schedule reactivated.',
        // Backend: cancelled=False, status=True
        { status: true, cancelled: false }
      )

    const cancelSchedule = async (item) => {
      const { isConfirmed } = await Vue.prototype.$swal({
        title: 'Cancel this schedule?',
        text:
          'Future runs will stop. The schedule is kept, and you can re-activate it later.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel schedule',
        cancelButtonText: 'Keep schedule'
      })
      if (!isConfirmed) return
      return runScheduleAction(
        item,
        'cancel',
        (id) => ExportLogService.cancel(id),
        'Schedule cancelled.',
        // Backend: cancelled=True, status=False (periodic_task removed)
        { cancelled: true, status: false }
      )
    }

    onMounted(async () => await ExportsService.getExports())

    return {
      isProcessing,
      rows,
      filteredRows,
      tableFields,
      selectedItem,
      columnSearch,
      columnSearchDraft,
      selectOptionsFor,
      applyColumnSearch,
      clearColumnSearch,
      hasColumnSearch,
      selectItem,
      deleteItem,
      onFullExport,
      formatDay,
      statusVariant,
      amendSchedule,
      pauseSchedule,
      resumeSchedule,
      activateSchedule,
      cancelSchedule
    }
  }
})
</script>

<style lang="scss" scoped>
.srs-heading {
  font-size: 1rem;
  color: #1a3a4a;
  letter-spacing: 0;
}

.srs-table {
  max-height: 400px;
  overflow-y: auto;
  width: auto;
  min-width: 100%;

  ::v-deep tbody tr {
    cursor: pointer;
  }
}

/* Header: teal uppercase labels with a dark underline — matches Export Log. */
::v-deep .srs-table thead th {
  background-color: #fff !important;
  color: #1a3a4a !important;
  border-top: 1px solid #e9ecef !important;
  border-bottom: 2px solid #212529 !important;
  vertical-align: middle;
  font-size: 0.875rem;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: uppercase;
  padding: 0.5rem 1rem;
  white-space: nowrap;
}

/* Tighter body cells to match the Export Log row sizing/gap. */
::v-deep .srs-table tbody td {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  vertical-align: middle;
}

/* Header per-column search icon — dark-teal on the light header. */
.header-search-btn {
  color: #1a3a4a !important;
  line-height: 1;
}
.header-search-btn:hover {
  color: #0f2530 !important;
}
.header-search-btn.is-active {
  color: #1b7a4b !important; /* highlight when a search is applied */
}

/* Apply button in the header search popover — brand metal-green. */
::v-deep .limeiq-apply-btn {
  background-color: #1a3a4a !important;
  border-color: #1a3a4a !important;
  color: #fff !important;
}

.action-links .action-sep {
  color: #d0d5dd;
  margin: 0 8px;
  font-size: 0.8rem;
}
.action-links .action-brand {
  color: #1a3a4a !important;
  font-weight: 500;
}
.action-links .action-brand:hover {
  color: #0f2530 !important;
}
/* Fixed-width block for the text actions so the trash icon after it always
   lands in the same column, regardless of how many text links a row has
   (Amend·Pause·Cancel vs Reactivate vs none). */
.action-links .action-text {
  display: flex;
  align-items: center;
  width: 200px;
  flex: 0 0 200px;
}
.action-links .action-delete {
  flex: 0 0 auto;
}

/* Status pills — same pale palette as the Export Log page. */
::v-deep .status-badge {
  font-weight: 500;
}
::v-deep .status-badge.status-active {
  background-color: #1a3a4a !important;
  color: #fff !important;
}
::v-deep .status-badge.status-paused {
  background-color: #e9ecef !important;
  color: #5a6268 !important;
}
::v-deep .status-badge.status-cancelled {
  background-color: #fadbd8 !important;
  color: #a93226 !important;
}
::v-deep .status-badge.status-saved {
  background-color: #d6e9f5 !important;
  color: #1a5276 !important;
}
::v-deep .status-badge.status-completed {
  background-color: #d6f5e3 !important;
  color: #1b7a4b !important;
}
</style>

<style lang="scss">
/* Popover renders at the document root (outside scoped styles), so its width
   rule is global — matches the Export Log page. */
.header-search-popover {
  min-width: 220px;
}
</style>
